import { useQueryClient } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import { toast } from 'sonner'

import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import { apiErrorDetail } from '../../lib/apiError'
import { CacheKeys } from '../../lib/cache'
import { type InsuranceIndication, syncCorgi } from '../../models/insuranceIndication'

export const CORGI_UP_TO_DATE_MESSAGE = 'Corgi looks up to date. Sync anyway?'
// The card refreshes this often while the sync job runs, and gives up after the wait.
const SYNC_POLL_MS = 5_000
const SYNC_WAIT_MS = 120_000

/** Staff only: ask the sync job to read this Corgi quote again, optionally linked to a pasted quote ID. */
export function SyncWithCorgiButton({
  orgId,
  applicationId,
  indication,
  needReason,
}: {
  orgId: string
  /** Needed to refresh the application query the card reads. */
  applicationId?: string
  indication: InsuranceIndication
  /** Why a sync looks needed; null when Corgi looks up to date. */
  needReason: string | null
}) {
  const queryClient = useQueryClient()
  const [open, setOpen] = useState(false)
  const [quoteId, setQuoteId] = useState('')
  const [isSending, setIsSending] = useState(false)
  // The run stamps corgiSyncAnsweredAt when it answers, so the card shows Syncing… until that changes.
  const answeredAt: string | null = indication.metadata.corgiSyncAnsweredAt ?? null
  const [answeredAtWhenPressed, setAnsweredAtWhenPressed] = useState<string | null | undefined>(undefined)
  const isSyncing = answeredAtWhenPressed !== undefined && answeredAtWhenPressed === answeredAt

  useEffect(() => {
    if (!isSyncing) return
    const startedAt = Date.now()
    const timer = setInterval(() => {
      if (Date.now() - startedAt > SYNC_WAIT_MS) {
        setAnsweredAtWhenPressed(undefined)
        return
      }
      void queryClient.invalidateQueries({ queryKey: [CacheKeys.APPLICATION, applicationId] })
      void queryClient.invalidateQueries({ queryKey: [CacheKeys.ORGANISATIONS, orgId, 'application'] })
    }, SYNC_POLL_MS)
    return () => clearInterval(timer)
  }, [isSyncing, queryClient, applicationId, orgId])

  function handleOpenChange(next: boolean) {
    if (!next) setQuoteId('')
    setOpen(next)
  }

  async function handleSync() {
    setIsSending(true)
    try {
      await syncCorgi(orgId, indication.id, quoteId.trim())
      setAnsweredAtWhenPressed(answeredAt)
      handleOpenChange(false)
    } catch (error) {
      toast.error(apiErrorDetail(error, 'Could not start the sync with Corgi'))
    } finally {
      setIsSending(false)
    }
  }

  const button = (
    <Button
      size="sm"
      variant={needReason ? 'default' : 'outline'}
      loading={isSyncing ? 'Syncing…' : undefined}
      onClick={() => setOpen(true)}
    >
      Sync with Corgi
    </Button>
  )

  return (
    <>
      {needReason && !isSyncing ? (
        <Tooltip>
          <TooltipTrigger asChild>{button}</TooltipTrigger>
          <TooltipContent>{needReason} Sync to update.</TooltipContent>
        </Tooltip>
      ) : (
        button
      )}
      <Dialog open={open} onOpenChange={handleOpenChange}>
        <DialogContent showCloseButton={false}>
          <DialogHeader>
            <DialogTitle>Sync with Corgi</DialogTitle>
            <DialogDescription>
              {needReason
                ? `${needReason} Cai will read this quote from Corgi again.`
                : CORGI_UP_TO_DATE_MESSAGE}
            </DialogDescription>
          </DialogHeader>
          <div className="flex flex-col gap-2">
            <Label htmlFor={`corgi-quote-id-${indication.id}`}>Corgi quote ID (optional)</Label>
            <Input
              id={`corgi-quote-id-${indication.id}`}
              value={quoteId}
              placeholder={indication.vendorId || undefined}
              onChange={(event) => setQuoteId(event.target.value)}
              disabled={isSending}
            />
            <span className="text-muted-foreground text-xs">
              Paste a quote ID from the Corgi portal to link this card to that quote.
            </span>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => handleOpenChange(false)} disabled={isSending}>
              Cancel
            </Button>
            <Button onClick={handleSync} loading={isSending ? 'Syncing…' : undefined}>
              {needReason ? 'Sync' : 'Sync anyway'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}
